<template>
  <div :class="classes" :data-preload="[$options.name]">
    <div
      v-if="showSelector"
      class="selectWrapper"
      :class="{
        'selectWrapper--focused': selected,
        'selectWrapper--no-divider': !hasVerticalDivider,
        'selectWrapper--darker': darker,
      }"
    >
      <label v-if="hasLabel" class="label" :for="id">
        {{ label }}
      </label>

      <select
        :id="id"
        v-model="selectedValue"
        class="select qa-select"
        :class="{
          'select--focused': selected,
          'select--no-label': !hasLabel,
        }"
        @change="onSelectChange()"
        @focus="onFocus"
        @blur="onBlur"
      >
        <option
          v-for="(choice, index) in choices"
          :key="index"
          :value="choice.value"
          :selected="value === choice.value"
        >
          {{ choice.label }}
        </option>

        <option v-if="threshold" :value="threshold">{{ threshold }}+</option>
      </select>

      <ArrowDropDownSvg class="icon" :original="true" height="7" width="8" />
    </div>
    <div
      v-if="!showSelector"
      class="inputWrapper"
      :class="{ 'inputWrapper--focused': selected }"
    >
      <label class="label" :for="id">
        {{ label }}
      </label>

      <input
        :id="id"
        v-model="inputValue"
        class="input"
        :class="{ 'input--focused': selected }"
        pattern="\d*"
        maxLength="3"
        type="number"
        @change="onInputChange()"
        @focus="onFocus"
        @blur="onBlur"
      />

      <ReloadSvg class="icon" :original="true" height="30" width="25" />
    </div>
  </div>
</template>

<script>
import noop from 'lodash/noop';
import ArrowDropDownSvg from 'Components/00-generated/ArrowDropDownSvg';
import ReloadSvg from 'Components/00-generated/ReloadSvg';
import globalMixin from 'Libs/mixins/globalMixin';

export default {
  name: 'QuantitySelector',
  components: {
    ArrowDropDownSvg,
    ReloadSvg,
  },
  mixins: [globalMixin],
  props: {
    darker: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    hasLabel: {
      type: Boolean,
      default: true,
    },
    hasVerticalDivider: {
      type: Boolean,
      default: true,
    },
    value: {
      type: Number,
      default: 0,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    choices: {
      type: Array,
      default: () => [
        {
          label: '1',
          value: '1',
        },
        {
          label: '2',
          value: '2',
        },
        {
          label: '3',
          value: '3',
        },
        {
          label: '4',
          value: '4',
        },
        {
          label: '5',
          value: '5',
        },
        {
          label: '6',
          value: '6',
        },
        {
          label: '7',
          value: '7',
        },
        {
          label: '8',
          value: '8',
        },
        {
          label: '9',
          value: '9',
        },
      ],
    },
    threshold: {
      type: Number,
      default: 10,
    },
    changeQuantity: {
      type: Function,
      default: noop,
    },
    productId: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    selectedValue: 1,
    inputValue: '',
    showSelector: true,
    selected: false,
    id: null,
  }),
  watch: {
    value(newValue) {
      if (this.value > 9) {
        this.inputValue = newValue;
        this.onInputChange();
      }
    },
  },
  mounted() {
    this.selectedValue = this.value || 1;
    this.inputValue = this.value || '';
    this.id = this.generateUID('quantitySelector-');

    this.showSelector = this.value <= 9;
  },
  methods: {
    onSelectChange() {
      if (this.selectedValue > 9) {
        this.showSelector = false;
        this.inputValue = this.selectedValue;
      } else {
        this.showSelector = true;
      }

      this.changeQuantity({
        productId: this.productId,
        quantity: this.selectedValue,
      });
    },
    onInputChange() {
      const parsedInput = parseInt(this.inputValue, 10);
      if (!Number.isInteger(parsedInput) || parsedInput <= 0) {
        this.inputValue = this.choices[0].value;
      }

      if (this.inputValue > 9) {
        this.showSelector = false;
      } else {
        this.showSelector = true;
        this.selectedValue =
          (this.choices.some((choice) => choice.value === this.inputValue) &&
            this.inputValue) ||
          this.choices[0].value;
      }

      this.changeQuantity({
        productId: this.productId,
        quantity: this.inputValue,
      });
    },
    onFocus() {
      this.selected = true;
    },
    onBlur() {
      this.selected = false;
    },
  },
};
</script>

<style scoped lang="scss">
@import 'variables/breakpoints';
@import 'mixins';

.quantity-selector-placeholder {
  min-height: 48px;
}

.quantitySelector {
  min-width: 72px;

  &--error {
    .error,
    .label {
      color: var(--color-red);
    }

    .selectWrapper {
      &::before {
        border-left-color: var(--color-red);
      }
    }
  }

  &--disabled {
    pointer-events: none;

    .selectWrapper,
    .inputWrapper {
      &::before {
        content: none;
      }
    }

    .select,
    .input {
      color: var(--color-rolling-stone);
    }

    .icon {
      display: none;
    }
  }

  &--showInput {
    .selectWrapper {
      display: none;
    }

    .inputWrapper {
      display: block;
    }
  }

  &--pending {
    .inputWrapper {
      .icon {
        fill: var(--color-tangerine);
      }
    }
  }

  &--centeredIcon {
    .icon {
      right: 15px;

      &.reloadSvg {
        right: 7px;
      }
    }
  }

  &--centeredIcon {
    .icon {
      right: 15px;

      &.reloadSvg {
        right: 7px;
      }
    }
  }

  .select,
  .input {
    padding-right: 35px;
  }

  .icon {
    right: 20px;
  }

  &--slim {
    .selectWrapper,
    .inputWrapper {
      &::before {
        content: '';
        width: 35px;
      }
    }

    .icon {
      right: 14px;
    }
  }
}

select::-ms-expand {
  display: none;
}

.selectWrapper,
.inputWrapper {
  position: relative;
  cursor: pointer;

  &::before {
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    width: 43px;
    height: 100%;

    border-left: 1px solid var(--color-alto);

    transition: border var(--time-S) ease-in-out;
  }

  &--focused::before {
    border-color: var(--color-chathamsblue);
  }
}

.selectWrapper {
  @include themeColor(
    color,
    var(--color-gold-drop),
    var(--color-endeavour),
    var(--color-chathamsblue)
  );

  &::before,
  .icon {
    pointer-events: none;
  }

  &--no-divider {
    &::before {
      border: none;
    }
  }

  &--darker {
    .select {
      color: var(--color-nero) !important;
      border-color: var(--color-silver-lining) !important;
    }
  }
}

.inputWrapper {
  .icon {
    fill: var(--color-black);
  }
}

.label {
  display: block;
  position: absolute;
  top: 5px;
  left: 8px;

  font-size: var(--font-size-XS);
  color: var(--color-black);

  &--required::after {
    content: '*';
  }
}

.select,
.input {
  appearance: none;
  outline: none;
  background: var(--color-white);

  padding-right: 53px;
  padding-left: 8px;
  height: 40px;
  padding-top: 14px;
  width: 100%;
  min-width: 45px;
  transition: all var(--time-S);

  font-weight: bold;
  font-size: var(--font-size-M);
  line-height: 16px;

  border: 1px solid var(--color-alto) !important;
  border-radius: var(--border-radius-default) !important;

  @include themeColor(
    color,
    var(--color-gold-drop),
    var(--color-endeavour),
    var(--color-chathamsblue)
  );

  &--focused {
    border: 1px solid var(--color-chathamsblue) !important;
  }
}

.select {
  border-color: var(--color-red);
  -webkit-appearance: none;
  cursor: pointer;

  &:focus::-ms-value {
    background: var(--color-white);

    color: var(--color-primary);
  }

  &--no-label {
    padding-top: 0;
  }
}

.input {
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
}

.icon {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translate(0, -50%);

  transition: fill var(--time-S) ease-in-out;

  &.reloadSvg {
    right: 11px;
  }
}

.error,
.message {
  padding-top: var(--space-0--half);
  padding-left: var(--space-XXS);

  color: var(--color-black--primary);
  font-size: var(--font-size-S);
  line-height: 16px;
}

@media print {
  .quantitySelector {
    &--disabled {
      width: 60px;

      .select,
      .input {
        width: 60px;
      }
    }
  }
}
</style>
